import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { ANALYTICS_EVENTS } from '@/helpers/analyticsEvents';
import { usePostHogIdentification } from '@/hooks/usePostHogIdentification';
import PasswordInput from '@/components/ui/PasswordInput';
import Button from '@/components/ui/Button';
import Alert, { AlertType } from '@/components/ui/Alert';
import styles from './AuthPages.module.scss';

const RenewForgotPasswordComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { renewForgotPassword } = useAuthContext();
  const { captureEvent } = usePostHogIdentification();
  const [formData, setFormData] = useState({ newPassword: '' });
  const [errors, setErrors] = useState<{ newPassword?: string }>({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alertMessage, setAlertMessage] = useState<{ message: string; type: AlertType }>({ message: '', type: 'info' });
  const [searchParams] = useSearchParams();

  const email = searchParams.get('email');
  const verificationToken = searchParams.get('token');

  const validateForm = () => {
    const newErrors: { newPassword?: string } = {};
    if (!formData.newPassword) {
      newErrors.newPassword = t('password') + ' ' + t('required');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitted(true);
    setAlertMessage({ message: '', type: 'info' });

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await renewForgotPassword(email as string, formData.newPassword, verificationToken as string);
      captureEvent(ANALYTICS_EVENTS.PASSWORD_RESET_COMPLETED);
      navigate('/login');
    } catch (error: unknown) {
      setAlertMessage({ message: (error as Error).message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.formContainer}>
      <form onSubmit={handleSubmit} className={styles.form} aria-labelledby="renew-password-heading">
        {/* HAT-1200: h1 קיים בכל דפי ה-auth האחרים (login/signup/forgot).
            נשאר sr-only בדסקטופ, נחשף כותרת דף במובייל דרך אותה שכבת SCSS
            (AuthPages.module.scss, בלוק ≤767px). מספק לקוראי מסך את שם
            המסך גם בזרימת האיפוס, שהייתה עד עכשיו חסרת heading. */}
        <h1 id="renew-password-heading" className="sr-only">
          {t('newPassword')}
        </h1>

        {alertMessage.message && <Alert type={alertMessage.type} message={alertMessage.message} />}

        <div className={styles.field}>
          <PasswordInput
            name="newPassword"
            placeholder={t('newPassword')}
            aria-label={t('newPassword')}
            value={formData.newPassword}
            onChange={handleChange}
            error={submitted && !!errors.newPassword}
            autoComplete="new-password"
          />
          {submitted && errors.newPassword && <p className={`${styles.fieldError} body-sm-regular`}>{errors.newPassword}</p>}
        </div>

        <div className={styles.buttonsContainer}>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? (
              <>
                <span className={styles.spinner}></span> {t('sending')}
              </>
            ) : (
              t('submit')
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default RenewForgotPasswordComponent;
